
import { useState } from 'react';

interface EventReminderSignupProps {
  eventTitle: string;
  eventDate: string;
  eventMonth: string;
  eventDay: string;
}

export default function EventReminderSignup({
  eventTitle,
  eventDate,
  eventMonth,
  eventDay,
}: EventReminderSignupProps) {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    const form = e.currentTarget;
    const emailInput = form.querySelector<HTMLInputElement>('input[name="email"]');
    const reminderInput = form.querySelector<HTMLSelectElement>('select[name="reminder_timing"]');

    if (!emailInput?.value) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);

    const body = new URLSearchParams();
    body.append('email', emailInput.value);
    body.append('reminder_timing', reminderInput?.value || '1 day before');
    body.append('event_title', eventTitle);
    body.append('event_date', `${eventMonth} ${eventDay}`);

    try {
      await fetch('https://readdy.ai/api/form/d6jnfl7rgrhbthj8n22g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-8 mb-10 border border-teal-100">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 flex items-center justify-center bg-teal-600 rounded-xl">
          <i className="ri-notification-3-line text-white text-xl"></i>
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Get an Event Reminder</h2>
      </div>
      <p className="text-gray-600 mb-6 text-sm">
        Don't miss <strong>{eventTitle}</strong> on <strong>{eventMonth} {eventDay}</strong>. Enter your email and we'll send you a reminder before the event.
      </p>

      {submitted ? (
        <div className="flex items-center gap-4 bg-white rounded-xl px-6 py-5 shadow-sm border border-teal-100">
          <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0">
            <i className="ri-checkbox-circle-line text-teal-600 text-2xl"></i>
          </div>
          <div>
            <p className="font-semibold text-gray-900">You're all set!</p>
            <p className="text-gray-500 text-sm">We'll send you a reminder before <strong>{eventTitle}</strong>.</p>
          </div>
        </div>
      ) : (
        <form
          data-readdy-form
          id="event-reminder-signup"
          onSubmit={handleSubmit}
          className="flex flex-col sm:flex-row gap-3"
        >
          <input
            type="email"
            name="email"
            required
            placeholder="Your email address"
            className="flex-1 border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-400 bg-white"
          />
          <select
            name="reminder_timing"
            className="border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-400 bg-white cursor-pointer"
          >
            <option value="1 day before">1 day before</option>
            <option value="2 days before">2 days before</option>
            <option value="1 week before">1 week before</option>
          </select>
          <button
            type="submit"
            disabled={loading}
            className="bg-teal-600 text-white px-6 py-3 rounded-lg text-sm font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 flex items-center gap-2 justify-center"
          >
            {loading ? (
              <><i className="ri-loader-4-line animate-spin"></i> Sending…</>
            ) : (
              <><i className="ri-bell-add-line"></i> Remind Me</>
            )}
          </button>
        </form>
      )}

      {error && (
        <p className="mt-3 text-sm text-red-500 flex items-center gap-1">
          <i className="ri-error-warning-line"></i> {error}
        </p>
      )}
    </div>
  );
}
